export const LEGAL_INFO_TITLE_KEY = "legal.info.title";
export const LEGAL_INFO_LEAD_KEY = "legal.info.lead";
export const LEGAL_INFO_PATIENT_TITLE_KEY = "legal.info.patientTitle";
export const LEGAL_INFO_PATIENT_BODY_KEY = "legal.info.patientBody";
export const LEGAL_INFO_APPOINTMENT_TITLE_KEY = "legal.info.appointmentTitle";
export const LEGAL_INFO_APPOINTMENT_BODY_KEY = "legal.info.appointmentBody";
export const LEGAL_INFO_LINKS_KEY = "legal.info.links";

export type LegalLinkItem = {
  label: string;
  href: string;
  download?: boolean;
};

export type LegalTextBlock =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] };

export const DEFAULT_LEGAL_INFO_TITLE = "Правовая информация";

export const DEFAULT_LEGAL_INFO_LEAD =
  "На этой странице собрана базовая информация для пациентов: порядок записи на прием, способы оплаты и ссылки на документы.";

export const DEFAULT_LEGAL_INFO_PATIENT_TITLE = "Информация для пациентов";

export const DEFAULT_LEGAL_INFO_PATIENT_BODY = `ООО "Ю-ДЕНТ" оказывает платные стоматологические услуги.

Доступные способы оплаты:
- наличными;
- банковскими картами;
- переводом на расчетный счет.`;

export const DEFAULT_LEGAL_INFO_APPOINTMENT_TITLE = "Правила записи на прием";

export const DEFAULT_LEGAL_INFO_APPOINTMENT_BODY = `Записаться на прием к врачу клиники Вы можете:
- лично обратившись в регистратуру по адресу: {{address}};
- позвонив по телефону в регистратуру стоматологии: {{phones}};
- оставив заявку на нашем сайте.

Обратите внимание: для подтверждения записи с вами обязательно свяжется администратор клиники.

При первичном посещении в день приема пациенту рекомендуется за 15–20 минут до визита обратиться в регистратуру для оформления медицинской карты и заключения договора.`;

export const DEFAULT_LEGAL_INFO_LINKS: LegalLinkItem[] = [
  {
    label: "Официальный интернет-портал правовой информации",
    href: "http://pravo.gov.ru/",
    download: false,
  },
  {
    label: "Официальный сайт Министерства здравоохранения Российской Федерации",
    href: "https://minzdrav.gov.ru/",
    download: false,
  },
];

export function fillLegalTemplate(text: string, vars: Record<string, string>) {
  return (text || "").replace(/\{\{(\w+)\}\}/g, (_, key) => vars[key] ?? "");
}

export function parseLegalTextBlocks(text: string): LegalTextBlock[] {
  const lines = (text || "")
    .split(/\r?\n/g)
    .map((line) => line.trim());

  const blocks: LegalTextBlock[] = [];
  let paragraphBuffer: string[] = [];
  let listBuffer: string[] = [];

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    blocks.push({
      type: "paragraph",
      text: paragraphBuffer.join(" ").trim(),
    });
    paragraphBuffer = [];
  };

  const flushList = () => {
    if (!listBuffer.length) return;
    blocks.push({
      type: "list",
      items: [...listBuffer],
    });
    listBuffer = [];
  };

  for (const line of lines) {
    const bulletMatch = line.match(/^[-*•]\s+(.+)$/);

    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }

    if (bulletMatch) {
      flushParagraph();
      listBuffer.push(bulletMatch[1].trim());
      continue;
    }

    flushList();
    paragraphBuffer.push(line);
  }

  flushParagraph();
  flushList();

  return blocks;
}

export function parseLegalLinks(raw?: string): LegalLinkItem[] {
  if (!raw?.trim()) return DEFAULT_LEGAL_INFO_LINKS;

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_LEGAL_INFO_LINKS;

    const normalized = parsed
      .map((item) => ({
        label: typeof item?.label === "string" ? item.label.trim() : "",
        href: typeof item?.href === "string" ? item.href.trim() : "",
        download: Boolean(item?.download),
      }))
      .filter((item) => item.label && item.href);

    return normalized.length ? normalized : DEFAULT_LEGAL_INFO_LINKS;
  } catch {
    return DEFAULT_LEGAL_INFO_LINKS;
  }
}

export function isExternalHref(href: string) {
  return /^https?:\/\//i.test((href || "").trim());
}